import { Step } from 'react-joyride';

import { TourDisplayBehavior } from './tourTracker';

export type TourId =
  | 'auth.signIn.email'
  | 'auth.signIn.password'
  | 'home-page'
  | 'portfolio.stratifications'
  | 'portfolio.dashboard'
  | 'portfolio.loan-level-data'
  | 'portfolio.loan-level-data.selection-status-line'
  | 'portfolio.loan-level-data.create-pool-button'
  | 'portfolio.loan-level-data.create-pool-modal';

/**
 * Delay in milliseconds between processing queued tours
 */
export const TOUR_TRANSITION_DELAY_MS = 100;

export type TourConfig = {
  steps: Step[];
  displayBehavior?: TourDisplayBehavior;
  useLocalStorage?: boolean;
};

export const TOUR_CONFIGS: Record<TourId, TourConfig> = {
  'auth.signIn.email': {
    steps: [
      {
        target: '[data-tour="email-input"]',
        content: 'Start by entering your institutional email address here.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 3,
    useLocalStorage: true,
  },
  'auth.signIn.password': {
    steps: [
      {
        target: '[data-tour="password-input"]',
        content: 'Now enter your secure password to sign in.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 'once',
    useLocalStorage: true,
  },
  'home-page': {
    steps: [
      {
        target: '[data-tour="home-nav"]',
        content:
          'The Home page is the ORSNN Dashboard, leading you to the 4 tabs that make up the platform.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="market-metrics-chart"]',
        content: 'Charts to track market metrics.',
        disableBeacon: true,
        placement: 'right',
      },
      {
        target: '[data-tour="interest-rates-dropdown"]',
        content: 'Choose to alternate between SOFR and the Treasury for interest rates.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="portfolio-overview"]',
        content: 'You can see your UPBs across asset classes.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="marketplace-blotter"]',
        content: 'Access to the 3 most recent offerings in the ORSNN Marketplace.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="notification-center"]',
        content: 'Access to platform activity. Notifications also sent via email.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="market-news"]',
        content: 'Stay current on the latest market news.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="document-library-upload"]',
        content:
          'Upload your transaction documents (LOI, MLPA etc.) to the document library for convenience.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.stratifications': {
    steps: [
      {
        target: 'body',
        content:
          'The Stratifications page is one of our premiere analytics tools, offering breakdowns of portfolio level metrics across your asset classes.',
        disableBeacon: true,
        placement: 'center',
        disableScrolling: true,
        styles: {
          tooltip: {
            position: 'fixed',
            top: '200px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
          spotlight: {
            display: 'none',
          },
        },
      },
      {
        target: '[data-tour="stratifications-portfolio-summary"]',
        content: 'You can view UPB amounts in total as well as by selected asset class.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="stratifications-strats-selection"]',
        content: 'Select a number of stratifications that are common in loan portfolios.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="stratifications-pill-filters"]',
        content: 'Filter for various criteria including FICO, geography, DTI, LTV, and more.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="stratifications-pool-selector"]',
        content:
          'You want to conduct analysis on a single pool within an asset class? Our pool selector allows you to choose.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="stratifications-first-strat-card"]',
        content:
          'ORSNN offers both chart and table view for conducting analysis of deal level stratifications. Each strat has a custom chart for convenient visibility.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.dashboard': {
    steps: [
      {
        target: 'body',
        content: 'The Dashboard features a view of important level metrics across asset classes.',
        disableBeacon: true,
        placement: 'center',
        disableScrolling: true,
        styles: {
          tooltip: {
            position: 'fixed',
            top: '200px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
          spotlight: {
            display: 'none',
          },
        },
      },
      {
        target: '[data-tour="dashboard-total-kpis"]',
        content:
          'These holdings are the same that you would see in "My Portfolio at a Glance" on the ORSNN Home Page.',
        disableBeacon: true,
        placement: 'bottom-start',
      },
      {
        target: '[data-tour="dashboard-resi-pools"]',
        content:
          'You get to see how many loan pools you have created in total and divided by asset class.',
        disableBeacon: true,
        placement: 'bottom-start',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.loan-level-data': {
    steps: [
      {
        target: 'body',
        content:
          'The Loan Level Data page provides detailed information about individual loans in your portfolio.',
        disableBeacon: true,
        placement: 'center',
        disableScrolling: true,
        styles: {
          tooltip: {
            position: 'fixed',
            top: '200px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
          spotlight: {
            display: 'none',
          },
        },
      },
      {
        target: '[data-tour="stratifications-portfolio-summary"]',
        content:
          'This is where you can view Loan Level Data of your holdings. Above you can see your UPB in total and by selected asset class.',
        disableBeacon: true,
        placement: 'left',
      },
      {
        target: '[data-tour="loan-level-data-side-nav-home"]',
        content:
          'Selecting your loans enables the option to create a pool for the selected loans, that pool can then be posted to the Marketplace for sale.',
        disableBeacon: true,
        placement: 'right',
      },
      {
        target: '[data-tour="loan-level-data-download-button"]',
        content: 'Always have the option to export to Excel and do work off platform if desired.',
        disableBeacon: true,
        placement: 'left',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.loan-level-data.selection-status-line': {
    steps: [
      {
        target: '[data-tour="loan-level-data-selection-status-line"]',
        content:
          'ORSNN automatically gives you the option to select all loans that fit the filter you created.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.loan-level-data.create-pool-button': {
    steps: [
      {
        target: '[data-tour="loan-level-data-create-pool-button"]',
        content: 'Here is where you will pool together your selected loans.',
        disableBeacon: true,
        placement: 'bottom',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
  'portfolio.loan-level-data.create-pool-modal': {
    steps: [
      {
        target: '[data-tour="create-pool-modal"]',
        content:
          'This pop up will appear when you select the "Create Pool" option. Here is where you will name your pool and select the origination type.',
        disableBeacon: true,
        placement: 'top',
      },
    ],
    displayBehavior: 'always',
    useLocalStorage: false,
  },
};
